import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { audienceMinerAccess } from "@/features/audience-miner/access/audience-miner-access";
import { vb360Access } from "@/features/vb360/access/vb360-access";
import { useVb360Context } from "@/features/vb360/hooks/use-vb360-context";
import { sessionDiagnosticsAccess } from "@/features/session-diagnostics/access/session-diagnostics-access";
import { platformErrorsAccess } from "@/features/observability/platform-errors/access/platform-errors-access";

type NavEntry = {
    to: string;
    label: string;
    icon: string;
};

type NavGroup = {
    label: string;
    items: NavEntry[];
};

const NAV_GROUPS: NavGroup[] = [
    {
        label: "Daily",
        items: [
            { to: "/command-center", label: "Command Center", icon: "monitor_heart" },
            { to: "/executive", label: "Executive", icon: "leaderboard" },
            { to: "/diagnostic", label: "Diagnostic", icon: "build" },
            { to: "/funnel-sla", label: "Funnel & SLA", icon: "filter_alt" },
        ],
    },
    {
        label: "Business",
        items: [
            { to: "/corridors", label: "Corridors & Clients", icon: "hub" },
            { to: "/revenue", label: "Revenue & Unit Economics", icon: "savings" },
            { to: "/customers", label: "Customers & Retention", icon: "groups" },
            { to: "/customer-intelligence", label: "Customer Intelligence", icon: "insights" },
            { to: "/meta-growth", label: "Meta Growth", icon: "campaign" },
        ],
    },
    {
        label: "Control",
        items: [
            { to: "/risk", label: "Risk, Treasury & Compliance", icon: "verified_user" },
            { to: "/investor", label: "Investor / Board", icon: "show_chart" },
            { to: "/experimental", label: "Experimental Lab", icon: "science" },
        ],
    },
    {
        label: "System",
        items: [
            { to: "/projector-health", label: "Projector Health", icon: "monitoring" },
            { to: "/shadow-parity", label: "Shadow Parity", icon: "difference" },
        ],
    },
];

const COLLAPSED_KEY = "vb-metrics.sidebar.collapsed";

// Storage can be unavailable (private mode, blocked site data): the bar then simply opens expanded.
const readCollapsed = (): boolean => {
    try {
        return window.localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
        return false;
    }
};

const writeCollapsed = (collapsed: boolean): void => {
    try {
        window.localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
        // The preference only lasts for this visit.
    }
};

const buildNavClass = ({ isActive }: { isActive: boolean }): string =>
    isActive ? "nav-item nav-item--active" : "nav-item";

const initialsOf = (name: string): string =>
    name
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase())
        .filter((char) => char !== "")
        .slice(0, 2)
        .join("");

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export const Sidebar: React.FC<Props> = ({ isOpen, onClose }) => {
    const { state, logout } = useAuth();
    const vb360 = useVb360Context();
    const platformErrors = platformErrorsAccess.useAccess();
    const user = state.user;
    const [menuOpen, setMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const footerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handleClick = (event: MouseEvent) => {
            if (footerRef.current === null) return;
            if (event.target instanceof Node && footerRef.current.contains(event.target)) return;
            setMenuOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [menuOpen]);

    const displayName =
        user === null
            ? ""
            : user.name !== ""
              ? user.name
              : user.preferredUsername !== ""
                ? user.preferredUsername
                : user.email;

    const scrimClass = isOpen ? "sidebar__scrim is-open" : "sidebar__scrim";
    const sidebarClass = ["sidebar", isOpen ? "is-open" : null, collapsed ? "is-collapsed" : null]
        .filter((name): name is string => name !== null)
        .join(" ");
    const toggleLabel = collapsed ? "Expandir la barra lateral" : "Contraer la barra lateral";
    const toggleCollapsed = (): void => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(next);
    };
    const navGroups = NAV_GROUPS.map((group) =>
        group.label === "Business" && audienceMinerAccess.can(state, "view")
            ? { ...group, items: [...group.items, { to: "/audience-miner/summary", label: "Audience Miner", icon: "travel_explore" }] }
            : group.label === "Daily" && sessionDiagnosticsAccess.can(state)
              ? { ...group, items: [...group.items, { to: "/diagnostic/sessions", label: "Live Sessions", icon: "live_tv" }] }
              : group
    ).map((group) =>
        group.label === "Business" && vb360Access.featureEnabled() === true && vb360.status === "ready"
            ? { ...group, items: [...group.items, { to: "/vb360", label: "VB360", icon: "person_search" }] }
            : group
    ).map((group) =>
        group.label === "System" && platformErrorsAccess.featureEnabled() === true && platformErrors === "allowed"
            ? { ...group, items: [...group.items, { to: "/platform-errors", label: "Errores de la plataforma", icon: "report" }] }
            : group
    );

    return (
        <>
            <div className={scrimClass} onClick={onClose} aria-hidden="true" />
            <aside className={sidebarClass}>
                <div className="sidebar__brand">
                    <img
                        className="sidebar__brand-wordmark"
                        src={`${process.env.PUBLIC_URL ?? ""}/assets/metrics-lockup-horizontal-dark.svg`}
                        alt="Metrics by VirtuaBroker"
                    />
                </div>

                {navGroups.map((group) => (
                    <div key={group.label} className="sidebar__group">
                        <div className="sidebar__group-label">{group.label}</div>
                        {group.items.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                aria-label={item.label}
                                title={item.label}
                                className={buildNavClass}
                                onClick={onClose}
                                end
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                ))}

                {user !== null ? (
                    <div className="sidebar__footer" ref={footerRef}>
                        <button
                            type="button"
                            className="sidebar__footer-trigger"
                            onClick={() => setMenuOpen((value) => !value)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                        >
                            <span className="av">{initialsOf(displayName)}</span>
                            <span className="sidebar__footer-meta">
                                <span className="sidebar__footer-name">{displayName}</span>
                                {user.email !== "" && user.email !== displayName ? (
                                    <span className="sidebar__footer-email">{user.email}</span>
                                ) : null}
                            </span>
                            <span className="material-symbols-outlined sidebar__footer-chevron">
                                {menuOpen ? "expand_more" : "expand_less"}
                            </span>
                        </button>

                        {menuOpen ? (
                            <div className="sidebar__footer-menu" role="menu">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="sidebar__footer-action"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        logout();
                                    }}
                                >
                                    <span className="material-symbols-outlined">logout</span>
                                    Log out
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <button
                    type="button"
                    className="sidebar__collapse"
                    onClick={toggleCollapsed}
                    title={toggleLabel}
                    aria-label={toggleLabel}
                >
                    <span className="material-symbols-outlined" aria-hidden="true">{collapsed ? "chevron_right" : "chevron_left"}</span>
                    <span className="sidebar__collapse-label">{collapsed ? "Expandir" : "Contraer"}</span>
                </button>
            </aside>
        </>
    );
};
